/**
 * Validação de idioma para o bootstrap da aplicação, sem depender de módulos
 * "React-adjacent" (ex.: auth/constants), de forma a manter o chunk inicial leve.
 *
 * A lista deve refletir `LANGUAGES_SUPPORTED` em
 * src/root-app/auth/constants/constants.ts.
 */

export const SUPPORTED_LANGS = ['pt-BR', 'es-ES', 'en-US'] as const;

export type SupportedLang = (typeof SUPPORTED_LANGS)[number];

/** Indica se o idioma informado é suportado pela aplicação. */
export function isSupportedLang(lang?: string | null): lang is SupportedLang {
  return !!lang && (SUPPORTED_LANGS as readonly string[]).includes(lang);
}

/**
 * Resolve o primeiro idioma suportado a partir das preferências do navegador
 * (`navigator.languages`, com fallback para `navigator.language`), fazendo match
 * exato (ex.: `pt-BR`) ou por idioma base (ex.: `pt`, `es-MX` -> `es-ES`).
 * Retorna `undefined` quando o navegador não informa nenhum idioma suportado,
 * permitindo que o chamador decida o fallback (ex.: default de ambiente).
 */
export function detectBrowserLanguage(): SupportedLang | undefined {
  if (typeof navigator === 'undefined') return undefined;

  const candidates =
    navigator.languages && navigator.languages.length > 0
      ? navigator.languages
      : [navigator.language];

  for (const candidate of candidates) {
    if (!candidate) continue;

    // Match exato (ex.: pt-BR)
    if (isSupportedLang(candidate)) return candidate;

    // Match por idioma base (ex.: "pt", "es-MX" -> "es-ES")
    const base = candidate.split('-')[0].toLowerCase();
    const matched = SUPPORTED_LANGS.find(
      (lang) => lang.split('-')[0].toLowerCase() === base,
    );
    if (matched) return matched;
  }

  return undefined;
}
